<template>
    <ValidationObserver v-slot="{ invalid }" slim>
        <form novalidate class="form-data" name="appointment_form" @submit.prevent="onSubmitForm">
            <div class="user-input">
                <TextField v-model="primaryInformation.company"
                :label="$t('checkResult:form.labels.company')"
                required/>
                <div class="form-group turnover-dropdown">
                    <SelectCustom v-model="primaryInformation.annualTurnover"
                    :options="timeTocallTurnoverOptions" :title="$t('checkResult:form.labels.dropdownMenu.turnover.turnoverText')"
                    required/>
                </div>
            </div>
            <div class="salutation">
                <div class="gender-salutation">{{$t('checkResult:form.labels.salutationText')}}</div>
                <div class="form-group gender-group">
                    <RadioSelect class="custom-style" v-model="primaryInformation.title" :options="salutationOptions" required inline/>
                </div>
            </div>
            <div class="user-input">
                <TextField v-model="primaryInformation.firstName"
                    :label="$t('checkResult:form.labels.firstname')"
                    :customLabelClass='"floating-label"'
                    required/>
                <TextField v-model="primaryInformation.lastName"
                    :label="$t('checkResult:form.labels.lastname')"
                    required/>
            </div>
            <div class="user-input">
                <TextField v-model="primaryInformation.email"
                    :label="$t('checkResult:form.labels.email')"
                    input-type="email"
                    required/>
                <TextField v-model="primaryInformation.phoneNumber"
                    :label="$t('checkResult:form.labels.phone')"
                    input-type="tel"
                    rules="phone"
                    required/>
            </div>
            <div class="user-input">
                <TextField v-model="primaryInformation.city"
                    :label="$t('checkResult:form.labels.city')"
                    :customLabelClass='"floating-label"'
                    required/>
                <TextField v-model="primaryInformation.zipCode"
                    :label="$t('checkResult:form.labels.zipCode')"
                    rules="zipLimit"
                    required/>
            </div>
        <div class="user-input">
            <SelectCustom v-model="primaryInformation.roleInCompany"
                :options="timeTocallFieldOptions" :title="$t('checkResult:form.labels.dropdownMenu.jobTitles.jobTitleText')"
                required/>
            <SelectCustom v-model="primaryInformation.companyFieldArea"
                :options="timeTocallJobOptions" :title="$t('checkResult:form.labels.dropdownMenu.companyFields.companyFieldText')"
                required/>
        </div>
        <div class="user-input custom-dropdown-valuechain">
            <SelectCustom v-model="primaryInformation.valueChain"
                :options="timeTocallValuechainOption" :title="$t('checkResult:form.labels.dropdownMenu.valueChain.titleText')"
                required/>
        </div>
        <div class="form-group message">
            <div class="form-field">
                <textarea id="message" name="message" class="form-control textarea-input" v-model="primaryInformation.message" :placeholder="$t('checkResult:form.labels.messagePlaceholder')"></textarea>
                <label class="floating-label-textarea textarea-label">{{$t('checkResult:form.labels.message')}}</label>
            </div>
        </div>
        <LegalText v-if="consent.content">
            <div class="form-consent">{{ consent.content }}</div>
            <div v-if="consent.links && consent.links.length > 0" class="term-links">
                <a v-for="(link, index) in consent.links"
                    :key="index"
                    class="link"
                    :href="link.url"
                    target="_blank">
                    {{ link.anchor }}
                </a>
            </div>
        </LegalText>
        <div v-if="consent.id" class="form-group required confirm-label">
            <label class="control-label" for="confirmLabel" v-html="consent.confirmLabel" />
            <ValidationProvider v-slot="{ errors }"
                                vid="consent"
                                tag="div"
                                class="custom-checkbox"
                                :rules="{ required: { allowFalse: false } }">
                <input id="consent"
                        type="checkbox"
                        class="custom-style"
                        v-model="consentCheck"
                        @keydown.enter.stop.prevent
                        required>
                <label for="consent" v-html="consent.confirmText" />
                <div class="errors" v-if="errors[0]">
                    <SvgIcon type="attention_bar"/>
                    {{ $t('error:required') }}
                </div>
            </ValidationProvider>
        </div>
        <div class="form-group meeting-setup">
            <ValidationProvider tag="div"
                                class="custom-checkbox"
                                >
                <input id="meeting-option"
                    type="checkbox"
                    class="custom-style"
                    @keydown.enter.stop.prevent
                    v-model="primaryInformation.meetingRequest">
                <label class="control-label meeting-option-label" for="meeting-option">{{ $t('checkResult:form.labels.terms1') }}</label>
            </ValidationProvider>
        </div>
        <div class="button-panel">
            <button type="submit"
                class="btn btn-red"
                :class="invalid || disabled ? 'inactive' : ''"
                :disabled="invalid || disabled">
                {{ nonQuizzers ? $t('checkResult:form.submit') : $t('checkResult:form.send') }}
                <SvgIcon type="horizontal_line_white" class="arrowicon line_white"/>
                <SvgIcon type="icon_arrow_right" class="arrowicon chevron"/>
            </button>
        </div>
        </form>
    </ValidationObserver>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import { HTTP } from '@/http-common'
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import { required, email, regex } from 'vee-validate/dist/rules'
import { phone, zipLimit } from '@/validation-rules'
import adobeTracking from '@/mixins/adobeTracking'
import SelectCustom from '@/components/form/SelectCustom'
import TextField from '@/components/form/TextField'
import RadioSelect from '@/components/form/RadioSelect'
import LegalText from '@/components/form/LegalText'
import SvgIcon from '@/components/SvgIcon'

extend('required', required)
extend('regex', regex)
extend('email', email)
extend('phone', phone)
extend('zipLimit', zipLimit)

export default {
    name: 'ContactUsForm',
    props: {
        nonQuizzers: Boolean
    },
    data: function() {
        return {
            primaryInformation: {
                company: '',
                annualTurnover: '',
                firstName: '',
                lastName: '',
                email: '',
                phoneNumber: '',
                city: '',
                zipCode: '',
                title: '',
                roleInCompany: '',
                companyFieldArea: '',
                valueChain: '',
                message: '',
                meetingRequest: false
            },
            consentCheck: false,
            disabled: false
        }
    },
    mixins: [adobeTracking],
    components: {
        ValidationObserver,
        ValidationProvider,
        SelectCustom,
        TextField,
        RadioSelect,
        LegalText,
        SvgIcon
    },
    mounted() {
        this.loadConsent()
    },
    methods: {
        ...mapActions('app', [
            'setLoading',
            'loadConsent'
        ]),
        async onSubmitForm() {
            this.disabled = true
            this.setLoading(true)
            const payload = {
                fields: {
                    ...this.primaryInformation,
                    gender: this.primaryInformation.title === 'mr' ? '1' : '2',
                    meetingRequest: this.primaryInformation.meetingRequest === true ? 'yes' : 'no',
                    result: this.nonQuizzers ? '' : this.getAllData,
                    consent: this.consent.id
                },
                metaData: {
                    language: this.language,
                    formPageUrl: (document.referrer) ? document.referrer : document.location.href,
                    campID: this.appMetadata.campID,
                    channel: this.appMetadata.channel,
                    aecID: this.appMetadata.aecID
                }
            }
            try {
                await HTTP.post('/submit-form', payload)
                this.trackFormSuccess('form success | Thank-you-page')
                this.$router.push({
                    name: 'success'
                })
            } catch (error) {
                console.log('submitForm::catch()', error)
                this.setLoading(false)
            }
        }
    },
    computed: {
        ...mapState('app', {
            language: 'language',
            consent: 'consent',
            appMetadata: 'metadata'
        }),
        ...mapGetters('userdata', [
            'timeTocallFieldOptions',
            'timeTocallTurnoverOptions',
            'timeTocallJobOptions',
            'salutationOptions',
            'timeTocallValuechainOption'
        ]),
        resultsOperational() {
            return this.$store.getters['answers/operational']
        },
        answers() {
            return this.$store.getters['answers/saveAnswer']
        },
        resultsStructural() {
            return this.$store.getters['answers/structural']
        },
        resultsEmotional() {
            return this.$store.getters['answers/emotional']
        },
        resultsSustainable() {
            return this.$store.getters['answers/sustainable']
        },
        getAllData() {
            let data = []
            Object.values(this.resultsOperational).reduce((result, obj) => {
                data.push('bo' + obj.step + ':' + obj.ans)
            }, {})
            Object.values(this.resultsStructural).reduce((result, obj) => {
                data.push('cs' + obj.step + ':' + obj.ans)
            }, {})
            Object.values(this.resultsEmotional).reduce((result, obj) => {
                data.push('pr' + obj.step + ':' + obj.ans)
            }, {})
            Object.values(this.resultsSustainable).reduce((result, obj) => {
                data.push('sr' + obj.step + ':' + obj.ans)
            }, {})

            return data.join(',')
        }
    }
}
</script>
